import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MultiSelectChangeEvent } from 'primeng/multiselect';
import { Table } from 'primeng/table';
import { BehaviorSubject, Observable, Subject, debounceTime, take, takeUntil } from 'rxjs';
import { AgencyService } from 'src/app/services/agency.service';
import { AlertService } from 'src/app/services/alert.service';
import { LoginService } from 'src/app/services/login.service';
import { LookupService } from 'src/app/services/lookup.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { GeographicFilterModel } from 'src/app/shared/models/geographic-filter-model';
import { NESSValidators } from 'src/app/shared/validators/ness.validators';

@Component({
  selector: 'ness-manage-cases-alert',
  templateUrl: './manage-cases-alert.component.html'
})
export class ManageCasesAlertComponent implements OnInit, OnDestroy {
  @ViewChild('alertTable', { static: false }) alertTable: Table;
  @Output() showManageAlertForm = new EventEmitter<boolean>();

  private _destroyed$ = new Subject<void>();

  public agenciesObj: BehaviorSubject<ServerModels.ENESS_REF_Agency[]> = new BehaviorSubject<ServerModels.ENESS_REF_Agency[]>([]);
  public agencies$: Observable<ServerModels.ENESS_REF_Agency[]> = this.agenciesObj.asObservable();
  public countiesObj: BehaviorSubject<ServerModels.ENESS_REF_Counties[]> = new BehaviorSubject<ServerModels.ENESS_REF_Counties[]>([]);
  public counties$: Observable<ServerModels.ENESS_REF_Counties[]> = this.countiesObj.asObservable();
  public statesObj: BehaviorSubject<ServerModels.ENESS_REF_State[]> = new BehaviorSubject<ServerModels.ENESS_REF_State[]>([]);
  public states$: Observable<ServerModels.ENESS_REF_State[]> = this.statesObj.asObservable();
  public fieldDivisionGroupsObj: BehaviorSubject<ServerModels.FieldDivisionGroupViewModel[]> = new BehaviorSubject<ServerModels.FieldDivisionGroupViewModel[]>([]);
  public fieldDivisionGroups$: Observable<ServerModels.FieldDivisionGroupViewModel[]> = this.fieldDivisionGroupsObj.asObservable();
  public fieldDivisionsObj: BehaviorSubject<ServerModels.ENESS_REF_FieldDivisions[]> = new BehaviorSubject<ServerModels.ENESS_REF_FieldDivisions[]>([]);
  public fieldDivisions$: Observable<ServerModels.ENESS_REF_FieldDivisions[]> = this.loginService.fieldDivisions$;

  loading: boolean = false;
  cols = [
    { field: 'CreatedOrModifiedDate', header: 'Created/Modified Date' },
    { field: 'AlertName', header: 'Alert Name' },
    { field: 'FieldDivisionName', header: 'Field Division' },
    { field: 'FieldDivisionGroupName', header: 'Field Division Group' },
    { field: 'StateName', header: 'State' },
    { field: 'CountyName', header: 'County(ies)' },
    { field: 'AgencyName', header: 'LE Agency' },
    { field: 'AlertsUserRequest_CrimeGunCasesID', header: 'Action' }
  ];
  rows: number = AppConstants.defaultTableRows;
  alertData: ServerModels.ENESS_AlertsUserRequest_CrimeGunCases = null;
  acFDGroups: ServerModels.ENESS_XREF_AlertCrimeGunCasesFieldDivisionGroups[] = [];
  acCounties: ServerModels.ENESS_XREF_AlertCrimeGunCasesCounties[] = [];
  acAgencies: ServerModels.ENESS_XREF_AlertCrimeGunCasesAgencies[] = [];
  tempData: ServerModels.ENESS_AlertsUserRequest_CrimeGunCases = null;

  alertsViewModel: ServerModels.CrimeGunCaseAlertsViewModel[] = [];
  selectedAlert: ServerModels.CrimeGunCaseAlertsViewModel;
  _userInfo: ServerModels.UserInfoViewModel = null;

  filterModel: GeographicFilterModel = new GeographicFilterModel('all', 'all', [], 'all', 'all', [], 'all', [], 'All', 'All', 'All', 'All', 'All', 'All', 'All', 'All');
  selectedFieldDivisionGroups$: Subject<ServerModels.FieldDivisionGroupViewModel[]> = new Subject<ServerModels.FieldDivisionGroupViewModel[]>();
  selectedCounties$: Subject<ServerModels.ENESS_REF_Counties[]> = new Subject<ServerModels.ENESS_REF_Counties[]>();

  maxAlertCount: number = 20;
  currentAlertCount: number = 0;

  public inEditMode: boolean = false;
  private existingAlertNames: string[] = [];
  private _currentUserFieldDivisionID: string = "";
  btnLabel: string = 'Add Alert';

  manageAlertsInputForm: FormGroup = this._fb.group({
    AlertsUserRequestID: '',
    ApplicationUserID: '',
    AlertName: ['', [Validators.required, NESSValidators.UniqueAlertName(this.existingAlertNames)]],
    FieldDivisionID: ['', Validators.required],
    StateID: '',
    FieldDivisionGroups: ([]),
    Counties: ({value: [], disabled: true}),
    Agencies: ({ value: [], disabled: true })
  });

  get alertNameErrorMessage(): string {
    const errorsObj = this.manageAlertsInputForm.get('AlertName').errors;
    let errorMessage: string = '';
    if (errorsObj['required']) {
      errorMessage = 'Please provide the alert name.';
    }
    if (errorsObj['alertNameValid']) {
      errorMessage = 'Notice:  An alert with that unique name already exits. Please choose another unique name.';
    }
    return errorMessage;
  }
  constructor(
    private loginService: LoginService,
    private lookupService: LookupService,
    private agencyService: AgencyService,
    private _fb: FormBuilder,
    private alertService: AlertService,
    public elementRef: ElementRef) { }

  ngOnInit(): void {
    this.loginService.userInfoData$.pipe(take(1)).subscribe((info: ServerModels.UserInfoViewModel) => {
      this._userInfo = info;
      if (!!info) {
        if (info.UserTypeDisplay === "ATF Field Division" && info.FieldDivisionID) {
          this._currentUserFieldDivisionID = info.FieldDivisionID;
          this.manageAlertsInputForm.get('FieldDivisionID').setValue(info.FieldDivisionID);
          this.manageAlertsInputForm.get('FieldDivisionID').updateValueAndValidity();
          this.setFilterValuesByFieldDivisionID(info.FieldDivisionID);
          this.SetInEditMode();
        } else if (info.UserTypeDisplay === "ATF HQ") {          
          this.manageAlertsInputForm.get('FieldDivisionID').setValue('8671595C-F3EA-4705-A772-7E4998D7FC30');
          this.manageAlertsInputForm.get('FieldDivisionID').updateValueAndValidity();
          this.setFilterValuesByFieldDivisionID('8671595C-F3EA-4705-A772-7E4998D7FC30') // Set Default value 'Atlanta'
        }

        this.alertService.GetCrimeGunAlertsByUser().subscribe((a: ServerModels.CrimeGunCaseAlertsViewModel[]) => {
          if (a) {
            this.currentAlertCount = a.length;
            this.alertsViewModel = a;
            this.existingAlertNames = this.alertsViewModel.map(a => a.AlertName);
            this.manageAlertsInputForm.get('AlertName').setValidators([Validators.required, NESSValidators.UniqueAlertName(this.existingAlertNames)]);
          }
        });
      }   
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
    this.selectedFieldDivisionGroups$.next(null);
    this.selectedFieldDivisionGroups$.complete();
    this.selectedCounties$.next(null);
    this.selectedCounties$.complete();
  }

  public onSelectFieldDivision(target: EventTarget): void {
    const fieldDivision: HTMLSelectElement = <HTMLSelectElement>target;
    this.setFilterValuesByFieldDivisionID(fieldDivision.value);
    this.SetInEditMode();

    this.manageAlertsInputForm.get('FieldDivisionGroups').setValue([]);
    this.manageAlertsInputForm.get('StateID').setValue("");    
    this.manageAlertsInputForm.get('Counties').setValue([]);
    this.manageAlertsInputForm.get('Agencies').setValue([]);
  }

  public onSelectFDGroup(evt: MultiSelectChangeEvent): void {
    if (this.selectedFieldDivisionGroups$.observers.length === 0) {
      this.selectedFieldDivisionGroups$.pipe(debounceTime(1000), takeUntil(this._destroyed$)).subscribe((groups: ServerModels.FieldDivisionGroupViewModel[]) => {
        const fieldDivisionID: string = this.manageAlertsInputForm.get('FieldDivisionID').value || 'all';
        const groupIds: string = groups.map(x => x.IntelligenceReportATFReferralGroupsID)?.join(';') || 'all';
        this.getStates(fieldDivisionID, groupIds);
        this.getCounties(fieldDivisionID, groupIds, 'all')
        this.getAgencies(fieldDivisionID, groupIds, 'all', 'all');
      },
        () => this.selectedFieldDivisionGroups$.complete());
    }

    this.selectedFieldDivisionGroups$.next(evt.value);
    this.manageAlertsInputForm.get('StateID').setValue("");
    this.manageAlertsInputForm.get('Counties').setValue([]);
    this.manageAlertsInputForm.get('Agencies').setValue([]);
    this.SetInEditMode();
  }

  public onSelectState(target: EventTarget): void {
    const state: HTMLSelectElement = <HTMLSelectElement>target;
    const fieldDivisionID: string = this.manageAlertsInputForm.get('FieldDivisionID').value || 'all';
    const groupIds: string = this.manageAlertsInputForm.get('FieldDivisionGroups').value?.map((x:any) => x.IntelligenceReportATFReferralGroupsID)?.join(';') || 'all';
    this.getCounties(fieldDivisionID, groupIds, state.value);
    this.getAgencies(fieldDivisionID, groupIds, state.value, 'all');
    this.manageAlertsInputForm.get('Counties').setValue([]);
    this.manageAlertsInputForm.get('Agencies').setValue([]);
    this.SetInEditMode();
  }

  public onSelectCounties(evt: MultiSelectChangeEvent): void {
    if (this.selectedCounties$.observers.length === 0) {
      this.selectedCounties$.pipe(debounceTime(1000), takeUntil(this._destroyed$)).subscribe((counties: ServerModels.ENESS_REF_Counties[]) => {
        const fieldDivisionID: string = this.manageAlertsInputForm.get('FieldDivisionGroups').value || 'all';
        const groupIds: string = this.manageAlertsInputForm.get('FieldDivisionGroups').value?.map((x:any) => x.IntelligenceReportATFReferralGroupsID)?.join(';') || 'all';
        const stateID: string = this.manageAlertsInputForm.get('StateID').value || 'all';
        const countyIds: string = counties.map(x => x.CountyID)?.join(';') || 'all';

        this.getAgencies(fieldDivisionID, groupIds, stateID, countyIds);
      },
        () => this.selectedCounties$.complete()
      );
    }

    this.selectedCounties$.next(evt.value);
    this.manageAlertsInputForm.get('Agencies').setValue([]);
  }

  private setFilterValuesByFieldDivisionID(fieldDivisionID: string) {
    this.getFieldDivisionGroups(fieldDivisionID);
    this.getStates(fieldDivisionID, 'all');
    this.getCounties(fieldDivisionID, 'all', 'all');
    this.getAgencies(fieldDivisionID, 'all', 'all', 'all');
  }

  public getFieldDivisionGroups(fieldDivisionId: string): void {
    if (!fieldDivisionId || fieldDivisionId.toLowerCase() === 'all') {
      this.lookupService.fdGroups$.subscribe((allGroups: ServerModels.FieldDivisionGroupViewModel[]) => {
        this.fieldDivisionGroupsObj.next(allGroups);
      });
    } else {
      this.lookupService.getFDGroupByFieldDivision(fieldDivisionId).pipe(take(1)).subscribe((groups: ServerModels.FieldDivisionGroupViewModel[]) => {
        this.fieldDivisionGroupsObj.next(groups);
      });
    }
  }

  private getStates(fieldDivisionId: string, fdGroups: string): void {
    if (!fieldDivisionId && !fdGroups) {
      this.statesObj.next([]);
      this.statesObj.complete();
    } else if ((!fieldDivisionId || fieldDivisionId.toLowerCase() === 'all') && (!fdGroups || fdGroups?.toLowerCase() === 'all')) {
      this.lookupService.usStates$.subscribe((states: ServerModels.ENESS_REF_State[]) => {
        this.statesObj.next(states);
      });
    } else {
      if (!!fdGroups && fdGroups?.toLowerCase() !== 'all') {
        this.getStatesForFieldDivisionGroups(fdGroups);
      } else {
        this.getStatesByFieldDivision(fieldDivisionId);
      }
    }
  }

  private getStatesForFieldDivisionGroups(fdGroups: string): void {
    if (!!fdGroups) {
      this.lookupService.getStatesForFieldDivisionGroups(fdGroups).pipe(take(1)).subscribe((groups: ServerModels.AgencyFieldDivisionGroupViewModel[]) => {
        if (groups) {
          let stateId = [...new Set(groups.map(x => x.StateID))];
          this.lookupService.usStates$.subscribe((states: ServerModels.ENESS_REF_State[]) => {
            let _states = states.filter(x => stateId.some(y => y == x.StateID))
            this.statesObj.next(_states);
          });
        }
      })
    } else {
      this.statesObj.next([]);
      this.statesObj.complete();
    }
  }

  private getStatesByFieldDivision(fieldDivisionId: string): void {
    this.statesObj.next([]);
    if (!fieldDivisionId) {
      this.statesObj.next([]);
    } else {
      this.lookupService.getStatesByFieldDivision(fieldDivisionId).pipe(take(1)).subscribe((states: ServerModels.ENESS_REF_State[]) => {
        this.statesObj.next(states);
      });
    }
  }

  private getCounties(fieldDivisionId: string, fdGroups: string, stateId: string): void {
    if (!fieldDivisionId && !fdGroups && !stateId) {
      this.countiesObj.next([]);
      this.countiesObj.complete()
    } else if ((!fieldDivisionId || fieldDivisionId.toLowerCase() === 'all') && (!fdGroups || fdGroups.toLowerCase() === 'all') && (!stateId || stateId.toLowerCase() === 'all')) {
      this.lookupService.counties$.pipe(take(1)).subscribe((allCounties: ServerModels.ENESS_REF_Counties[]) => {
        this.countiesObj.next(allCounties);
      });
    } else if (fieldDivisionId?.toLowerCase() !== 'all' && (!fdGroups || fdGroups?.toLowerCase() === 'all') && (!stateId || stateId?.toLowerCase() === 'all')) {
      this.getCountiesByFieldDivision(fieldDivisionId);
    } else if (!!fdGroups && fdGroups?.toLowerCase() !== 'all' && (!stateId || stateId?.toLowerCase() === 'all')) {
      this.getCountiesByFieldDivisionGroup(fdGroups);
    } else if (!!fdGroups && fdGroups?.toLowerCase() !== 'all' && (!stateId || stateId?.toLowerCase() !== 'all')) {
      this.getCountiesByStateAndFDGroup(fdGroups, stateId);
    } else if ((!fdGroups || fdGroups?.toLowerCase() === 'all') && (!!stateId && stateId.toLowerCase() !== 'all')) {
      this.getCountiesByState(stateId);
    }
  }

  private getCountiesByFieldDivision(fieldDivisionId: string): void {
    if (!fieldDivisionId) {
      this.countiesObj.next([]);
    } else {
      this.lookupService.getCountiesByFieldDivision(fieldDivisionId).pipe(take(1)).subscribe((counties: ServerModels.ENESS_REF_Counties[]) => {
        this.countiesObj.next(counties);
      });
    }
  }

  private getCountiesByFieldDivisionGroup(fieldDivisionGroupIds: string): void {
    if (!fieldDivisionGroupIds) {
      this.countiesObj.next([]);
    } else {
      this.lookupService.getCountiesForFieldDivisionGroup(fieldDivisionGroupIds).pipe(take(1)).subscribe((counties: ServerModels.ENESS_REF_Counties[]) => {
        this.countiesObj.next(counties);
      });
    }
  }

  private getCountiesByStateAndFDGroup(fdGroups: string, stateId: string): void {
    if (!stateId && !fdGroups) {
      this.countiesObj.next([]);
    } else {
      this.lookupService.getCountiesForFDGroupAndState(fdGroups, stateId).subscribe((counties: ServerModels.ENESS_REF_Counties[]) => {
        this.countiesObj.next(counties);
      });
    }
  }

  private getCountiesByState(stateId: string): void {
    if (!stateId) {
      this.countiesObj.next([]);
    } else {
      this.lookupService.getCountiesByState(stateId).subscribe((counties: ServerModels.ENESS_REF_Counties[]) => {
        this.countiesObj.next(counties);
      });
    }
  }

  private getAgencies(fieldDivisionId: string, fgGroupIds: string, stateId: string, countyIds: string): void {
    if (!fieldDivisionId && !fgGroupIds && !stateId && !countyIds) {
      this.agenciesObj.next([]);
    } else if (!!countyIds && countyIds?.toLowerCase() !== 'all' && !!fgGroupIds && fgGroupIds?.toLowerCase() !== 'all' && !!stateId && stateId?.toLowerCase() !== 'all') {
      this.getAgenciesByFDGroupStateCounty(fgGroupIds, countyIds, stateId);
    } else if (!!countyIds && countyIds?.toLowerCase() !== 'all' && !!fgGroupIds && fgGroupIds?.toLowerCase() !== 'all') {
      this.getAgenciesByFDGroupStateCounty(fgGroupIds, countyIds, 'all');
    } else if (!!fgGroupIds && fgGroupIds?.toLowerCase() !== 'all' && !!stateId && stateId?.toLowerCase() !== 'all') {
      this.getAgenciesByFDGroupStateCounty(fgGroupIds, '', stateId);
    } else if (!!fieldDivisionId && fieldDivisionId?.toLowerCase() !== 'all' && (!fgGroupIds || fgGroupIds?.toLowerCase() === 'all') && !!stateId && stateId?.toLowerCase() !== 'all') {
      this.getAgenciesByFDState(fieldDivisionId, stateId);
    } else if (!!countyIds && countyIds?.toLowerCase() !== 'all') {
      this.getAgenciesByCountyIds(countyIds);
    } else if (stateId?.toLowerCase() !== 'all') {
      this.getAgenciesByStateId(stateId);
    } else if (!!fgGroupIds && fgGroupIds?.toLowerCase() !== 'all') {
      this.getAgenciesByFieldDivisionGroup(fgGroupIds);
    } else if (!!fieldDivisionId && fieldDivisionId?.toLowerCase() !== 'all') {
      this.getAgenciesByFieldDivisionId(fieldDivisionId);
    } else {
      this.agenciesObj.next(this.agencyService.agenciesSubj.getValue());
    }
  }

  private getAgenciesByFDGroupStateCounty(fdGroups: string, countyIds: string, stateId: string): void {
    if (!fdGroups && !countyIds && !stateId) {
      this.agenciesObj.next([]);
    }
    this.lookupService.getAgenciesByFDGroupStateCounty(fdGroups, stateId, countyIds).pipe(take(1)).subscribe((agencies: ServerModels.AgencyViewModel[]) => {
      this.agenciesObj.next(<ServerModels.ENESS_REF_Agency[]>agencies);
    });
  }

  private getAgenciesByFDState(fieldDivisionId: string, stateId: string) {
    this.agencyService.getAgenciesForState(stateId).pipe(take(1)).subscribe((agencies: ServerModels.ENESS_REF_Agency[]) => {
      const _fdAgency = agencies.filter(x => x.FieldDivisionID == fieldDivisionId);
      this.agenciesObj.next(_fdAgency);
    });
  }

  private getAgenciesByCountyIds(countyIds: string): void {
    if (!countyIds) {
      this.agenciesObj.next([]);
    } else {
      this.lookupService.getAgenciesByCountyIds(countyIds).pipe(take(1)).subscribe((agencies: ServerModels.AgencyViewModel[]) => {
        this.agenciesObj.next(<ServerModels.ENESS_REF_Agency[]>agencies);
      });
    }
  }

  private getAgenciesByStateId(stateId: string): void {
    if (!stateId) {
      this.agenciesObj.next([]);
    }
    this.agencyService.getAgenciesForState(stateId).pipe(take(1)).subscribe((agencies:ServerModels.ENESS_REF_Agency[]) => {
      this.agenciesObj.next(agencies);
    });
  }

  private getAgenciesByFieldDivisionGroup(fieldDivisionGroupIds: string): void {
    if (!fieldDivisionGroupIds) {
      this.agenciesObj.next([]);
    }
    this.lookupService.getAgenciesByFieldDivisionGroupIds(fieldDivisionGroupIds).pipe(take(1)).subscribe((agencies: ServerModels.AgencyViewModel[]) => {
      this.agenciesObj.next(<ServerModels.ENESS_REF_Agency[]>agencies);
    });
  }

  private getAgenciesByFieldDivisionId(fieldDivisionId: string): void {
    if (!fieldDivisionId) {
      this.agenciesObj.next([]);
    }
    this.lookupService.getAgenciesByFieldDivisionId(fieldDivisionId).pipe(take(1)).subscribe((agencies: ServerModels.AgencyViewModel[]) => {
      this.agenciesObj.next(<ServerModels.ENESS_REF_Agency[]>agencies);
    });
  }

  private _setAlertCrimeGunCasesFieldDivisionGroups() {
    this.acFDGroups = [];
    const _dataMap = new Map<string, string | number>();
    if (this.alertData) {
      this.alertData.ENESS_XREF_AlertCrimeGunCasesFieldDivisionGroups.forEach(a => { _dataMap.set(a.IntelligenceReportATFReferralGroupsID, a.AlertCrimeGunCasesFieldDivisionGroupID )});
    }
    this.manageAlertsInputForm.get('FieldDivisionGroups').value?.forEach((g: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups) => {
      let fdGroup = {
        AlertCrimeGunCasesFieldDivisionGroupID: _dataMap.get(g.IntelligenceReportATFReferralGroupsID),
        AlertsUserRequest_CrimeGunCasesID: this.manageAlertsInputForm.get('AlertsUserRequestID').value,
        ENESS_AlertsUserRequest_CrimeGunCases: null,
        IntelligenceReportATFReferralGroupsID: g.IntelligenceReportATFReferralGroupsID,
        ENESS_REF_IntelligenceReportATFReferralGroups: null,
        CreatedOn: this.alertData ? this.alertData.CreatedOn : null,
        CreatedBy: this.alertData ? this.alertData.CreatedBy : null
      } as ServerModels.ENESS_XREF_AlertCrimeGunCasesFieldDivisionGroups;
      this.acFDGroups.push(fdGroup);
    })
  }

  private _setAlertCrimeGunCasesCounties() {
    this.acCounties = [];
    const _dataMap = new Map<string, string | number>();
    if (this.alertData) {
      this.alertData.ENESS_XREF_AlertCrimeGunCasesCounties.forEach(a => { _dataMap.set(a.CountyID, a.AlertCrimeGunCasesCountiesID) });
    }
    this.manageAlertsInputForm.get('Counties').value?.forEach((c: ServerModels.ENESS_REF_Counties) => {
      let county = {
        AlertCrimeGunCasesCountiesID: _dataMap.get(c.CountyID),
        AlertsUserRequest_CrimeGunCasesID: this.manageAlertsInputForm.get('AlertsUserRequestID').value,
        ENESS_AlertsUserRequest_CrimeGunCases: null,
        CountyID: c.CountyID,
        ENESS_REF_Counties: null,
        CreatedOn: this.alertData ? this.alertData.CreatedOn : null,
        CreatedBy: this.alertData ? this.alertData.CreatedBy : null
      } as ServerModels.ENESS_XREF_AlertCrimeGunCasesCounties;
      this.acCounties.push(county);
    })
  }

  private _setAlertCrimeGunCasesAgencies() {
    this.acAgencies = [];
    const _dataMap = new Map<string, string | number>();
    if (this.alertData) {
      this.alertData.ENESS_XREF_AlertCrimeGunCasesAgencies.forEach(a => { _dataMap.set(a.AgencyID, a.AlertCrimeGunCasesAgenciesID) });
    }
    this.manageAlertsInputForm.get('Agencies').value?.forEach((a: ServerModels.ENESS_REF_Agency) => {
      let agency = {
        AlertCrimeGunCasesAgenciesID: _dataMap.get(a.AgencyID),
        AlertsUserRequest_CrimeGunCasesID: this.manageAlertsInputForm.get('AlertsUserRequestID').value,
        ENESS_AlertsUserRequest_CrimeGunCases: null,
        AgencyID: a.AgencyID,
        ENESS_REF_Agency: null,
        CreatedOn: this.alertData ? this.alertData.CreatedOn : null,
        CreatedBy: this.alertData ? this.alertData.CreatedBy : null
      } as ServerModels.ENESS_XREF_AlertCrimeGunCasesAgencies;
      this.acAgencies.push(agency);
    })
  }

  private generateAlertsUserRequestCrimeGunCases(): ServerModels.ENESS_AlertsUserRequest_CrimeGunCases {
    this._setAlertCrimeGunCasesFieldDivisionGroups();
    this._setAlertCrimeGunCasesCounties();
    this._setAlertCrimeGunCasesAgencies();
    
    let alert = {
      AlertsUserRequest_CrimeGunCasesID: this.manageAlertsInputForm.get('AlertsUserRequestID').value,
      ApplicationUserID: this.manageAlertsInputForm.get('ApplicationUserID').value,
      AlertName: this.manageAlertsInputForm.get('AlertName').value,
      FieldDivisionID: this.manageAlertsInputForm.get('FieldDivisionID').value,
      StateID: this.manageAlertsInputForm.get('StateID').value,
      ENESS_REF_FieldDivision: null,
      ENESS_REF_State: null,
      ENESS_XREF_AlertCrimeGunCasesFieldDivisionGroups: this.acFDGroups,
      ENESS_XREF_AlertCrimeGunCasesCounties: this.acCounties,
      ENESS_XREF_AlertCrimeGunCasesAgencies: this.acAgencies,
      CreatedOn: this.alertData ? this.alertData.CreatedOn : null,
      CreatedBy: this.alertData ? this.alertData.CreatedBy : null
    } as ServerModels.ENESS_AlertsUserRequest_CrimeGunCases;

    return alert;
  }

  public onCancel(): void {
    this.resetForm();
    this.setFilterValuesByFieldDivisionID(this._currentUserFieldDivisionID);
  }

  public onSaveAlert() {
    this.loading = true;
    let alert = this.generateAlertsUserRequestCrimeGunCases();    
    this.tempData = alert;
    this.alertService.AddOrUpdateCrimeGunCaseAlert(alert).pipe(take(1)).subscribe(
      (data: ServerModels.CrimeGunCaseAlertsViewModel) => {        
        if (data) {     
          let srcIndex = this.alertsViewModel.findIndex(x => x.AlertsUserRequest_CrimeGunCasesID == data.AlertsUserRequest_CrimeGunCasesID);
          if (srcIndex > -1) {
            this.alertsViewModel.splice(srcIndex, 1);
          } else {           
            this.currentAlertCount = this.currentAlertCount + 1;
          }
          // Place the new item on the top
          this.alertsViewModel.unshift(data);

          this.existingAlertNames = this.alertsViewModel.map(a => a.AlertName);
          this.manageAlertsInputForm.get('AlertName').setValidators([Validators.required, NESSValidators.UniqueAlertName(this.existingAlertNames)]);
        }
        this.resetForm();
        this.setFilterValuesByFieldDivisionID(this._currentUserFieldDivisionID);
        this.loading = false;
      },
      (err:any) => this.loading = false
    );
  }

  public resolveCountyNames(county: ServerModels.ENESS_REF_Counties): string {
    return `${county.CountyName}, ${county.ENESS_REF_State.StateAbbreviation}`;
  }

  public SetInEditMode(): void {
    if (this.manageAlertsInputForm.get('FieldDivisionID').value !== 'all') {
      this.manageAlertsInputForm.get('Counties').enable();
      this.manageAlertsInputForm.get('Agencies').enable();
    }
    else {
      this.manageAlertsInputForm.get('Counties').disable();
      this.manageAlertsInputForm.get('Agencies').disable();
    }
  }

  public onRowEditAlert(alert: ServerModels.CrimeGunCaseAlertsViewModel, index: number): void  {
    this.alertData = null;
    this.resetForm();
    this.setFilterValuesByFieldDivisionID('all');
    this.alertService.GetCrimeGunAlertsById(alert.AlertsUserRequest_CrimeGunCasesID).pipe(takeUntil(this._destroyed$)).subscribe({
      next: (data: ServerModels.ENESS_AlertsUserRequest_CrimeGunCases) => {
        if (data) {
          this.alertData = data;
          this.UpdateFilterValueOnEdit(data);
          // Remove AlertName from the UniqueAlertName check
          let srcIndex = this.existingAlertNames.findIndex(x => x == data.AlertName);
          delete this.existingAlertNames[srcIndex];
          this.manageAlertsInputForm.get('AlertName').setValidators([Validators.required, NESSValidators.UniqueAlertName(this.existingAlertNames)]);
          this.manageAlertsInputForm.get('AlertName').updateValueAndValidity();
          
          this.inEditMode = true;
          this.btnLabel = 'Save';
        }
        this.loading = false;
      },
      complete: () => { this.loading = false; },
      error: (err:any) => { this.loading = false; },
    });
  }

  private updateFormValues(data: ServerModels.ENESS_AlertsUserRequest_CrimeGunCases): void {
    const formData = {
      AlertsUserRequestID: data.AlertsUserRequest_CrimeGunCasesID,
      ApplicationUserID: data.ApplicationUserID,
      AlertName: data.AlertName,
      FieldDivisionID: data.FieldDivisionID,
      StateID: data.StateID != null ? data.StateID : "",
      FieldDivisionGroups: data.ENESS_XREF_AlertCrimeGunCasesFieldDivisionGroups?.map(g => g.ENESS_REF_IntelligenceReportATFReferralGroups),
      Counties: data.ENESS_XREF_AlertCrimeGunCasesCounties?.map(c => c.ENESS_REF_Counties),
      Agencies: data.ENESS_XREF_AlertCrimeGunCasesAgencies?.map(a => a.ENESS_REF_Agency)
    };
    this.manageAlertsInputForm.setValue(formData);
    this.manageAlertsInputForm.updateValueAndValidity();
  }

  private UpdateFilterValueOnEdit(data: ServerModels.ENESS_AlertsUserRequest_CrimeGunCases) {
    const _fieldDivisionID: string = data.FieldDivisionID; 
    const _fdGroup: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups[] = data.ENESS_XREF_AlertCrimeGunCasesFieldDivisionGroups?.map(g => g.ENESS_REF_IntelligenceReportATFReferralGroups); 
    const fdGroupIds: string = _fdGroup?.map(x => x.IntelligenceReportATFReferralGroupsID)?.join(';') || 'all';
    const _stateID: string = data.StateID ? data.StateID : 'all';  
    const _counties: ServerModels.ENESS_REF_Counties[] = data.ENESS_XREF_AlertCrimeGunCasesCounties?.map(c => c.ENESS_REF_Counties); 
    const countyIds: string = _counties.map(x => x.CountyID)?.join(';') || 'all';

    this.getFieldDivisionGroups(_fieldDivisionID);
    this.getStates(_fieldDivisionID, fdGroupIds);
    this.getCounties(_fieldDivisionID, fdGroupIds, _stateID);
    this.getAgencies(_fieldDivisionID, fdGroupIds, _stateID, countyIds);
    this.updateFormValues(data);      
  }

  public onRowDeleteAlert(alert: ServerModels.CrimeGunCaseAlertsViewModel, index: number): void {
    this.loading = true;
    this.alertService.DeleteCrimeGunCaseAlertByID(alert.AlertsUserRequest_CrimeGunCasesID).pipe(takeUntil(this._destroyed$)).subscribe({
      next: (isDeleted:boolean) => {
        if (isDeleted) {
          this.alertsViewModel.splice(index, 1);

          this.existingAlertNames = this.alertsViewModel.map(a => a.AlertName);
          this.manageAlertsInputForm.get('AlertName').setValidators([Validators.required, NESSValidators.UniqueAlertName(this.existingAlertNames)]);

          if (this.alertsViewModel.length % 20 == 0) {
            this.alertTable.reset();
          }
          this.currentAlertCount = this.alertsViewModel.length;
        }
        this.loading = false;
      },      
      complete: () => { this.loading = false; },
      error: (err:any) => { this.loading = false; },
    });
  }

  private resetForm() {
    this.manageAlertsInputForm.reset({
      AlertsUserRequestID: null,
      ApplicationUserID: null,
      AlertName: '',
      FieldDivisionID: this._currentUserFieldDivisionID,
      StateID: '',
      FieldDivisionGroups: ([]),
      Counties: ([]),
      Agencies: ([])
    });
    this.btnLabel = 'Add Alert';
    this.inEditMode = false; //this._currentUserFieldDivisionID ? true : false;
    this.alertData = null;
  }
}
